import {
  ChevronLeft,
  ChevronRight,
  Home,
  Users,
  UserCheck,
  ShoppingCart,
  FileText,
  Layers,
  Calculator,
  Bot,
  User,
  LogOut,
  AlertCircle,
  ChevronsRight,
  Banknote,
  Gavel,
  Trophy,
  ShieldCheck,
  Sparkles,
  Newspaper,
  LayoutDashboard,
  XCircle,
  ArrowLeftRight,
  TrendingDown,
  Landmark,
  Wallet,
  MessagesSquare,
  ChevronDown,
  Check,
  SquareKanban,
  KeyRound,
  Handshake,
  ClipboardList,
  HandCoins,
} from "lucide-react";
import { Link, useLocation, matchPath } from "react-router";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetIdentity, useLogout } from "ra-core";
import { useQuery } from "@tanstack/react-query";
import { listarIndicacoes } from "../contratacao/dados";
import { useCorretoraVinculada } from "../seguros/portalAzos";
import { useExigenciasAbertas } from "../seguros/exigencias";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { useSidebar } from "./SidebarContext";
import { useBriefingNaoLido } from "../agents/useBriefingNaoLido";
import { cn } from "@/lib/utils";
import {
  CRM_ROUTE,
  HUB_ROUTE,
  PRODUCT_MODULES,
  getProductModuleConfig,
  otherProductModule,
  
} from "@/lib/product-module";
import { useProductModule } from "@/lib/useProductModule";
import { ehCliente, temModulo, useOrganizacaoAtiva } from "../organizacoes/useOrganizacaoAtiva";
import { agruparEmSecoes, rotaAcesa, secaoDaRota, type SidebarItem } from "./secoes";
import { rotaPermitidaLeitura, type PerfilLeitura } from "../commons/somenteLeitura";
import { useFeedNaoLidos } from "../feed/useFeedNaoLidos";
import { useConversas } from "../mensagens/useConversas";
import { mensagensVisiveis } from "../mensagens/liberacao";

/** Número vermelho no canto de um ícone — o mesmo desenho do sino. */
export const ContadorNovos = ({ total, anel = "ring-card" }: { total: number; anel?: string }) => (
  <span
    className={cn(
      "absolute -right-2 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2",
      anel,
    )}
  >
    {total > 9 ? "9+" : total}
  </span>
);

const SidebarBody = ({ collapsed }: { collapsed: boolean }) => {
  const { setCollapsed, isMobile } = useSidebar();
  const location = useLocation();
  const { identity } = useGetIdentity();
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const logout = useLogout();
  // Ponto de não-lido: o briefing é entregue numa página que ninguém visita
  // por hábito, então o menu avisa que chegou algo novo.
  const { naoLido: briefingNaoLido } = useBriefingNaoLido();
  // Número no ícone do Feed (posts não lidos, mesma fonte do sino) e no de
  // Mensagens (não lidas, migration 250) — pedido do Rick em 15/10/2026.
  const { total: feedNaoLidos } = useFeedNaoLidos();
  const verMensagens = mensagensVisiveis();
  const { total: mensagensNaoLidas } = useConversas({ ativo: verMensagens });

  const isActive = (path: string) =>
    matchPath(path, location.pathname) || matchPath(`${path}/*`, location.pathname);


  // Hook e não resolveProductModule direto: contato e cliente são dos dois
  // produtos, e a rota sozinha não diz de qual. O hook lembra de onde a pessoa
  // veio, então abrir um contato dentro de Seguros não joga o menu pro
  // Consórcio.
  const productModule = useProductModule() ?? "consortium";

  // "/" agora é o hub, então cada produto aponta pra própria raiz — que desde
  // 30/09/2026 é o Feed North (com a saudação e o resumo do dia). O antigo
  // Início virou "Dashboard", só com os números. Em Seguros o Dashboard lê a
  // carteira: do super admin (a rede) ou da corretora vinculada (a dela); para
  // os demais ele redirecionaria para a Cotação, que já tem item próprio.
  const baseItems: SidebarItem[] = [
    { label: "Feed", to: PRODUCT_MODULES[productModule].home, icon: <Newspaper className="w-5 h-5" />, contador: feedNaoLidos, modulo: "feed" },
    ...(verMensagens
      ? [{ label: "Mensagens", to: "/mensagens", icon: <MessagesSquare className="w-5 h-5" />, contador: mensagensNaoLidas, modulo: "mensagens" as const }]
      : []),
    {
      label: "Dashboard",
      to: PRODUCT_MODULES[productModule].dashboard,
      icon: <LayoutDashboard className="w-5 h-5" />,
      exigeCorretora: productModule === "insurance",
    },
    { label: "Contatos", to: "/contacts", icon: <Users className="w-5 h-5" /> },
    { label: "Clientes", to: "/clients", icon: <UserCheck className="w-5 h-5" /> },
    // O funil de volta no menu (Rodrigo, 28/10/2026): pela troca de produto da
    // barra superior eram dois cliques. /deals é rota compartilhada, então o
    // menu continua o do produto de onde a pessoa veio.
    { label: "CRM", to: CRM_ROUTE, icon: <SquareKanban className="w-5 h-5" /> },
  ];

  const consortiumItems: SidebarItem[] = [
    { label: "Vendas", to: "/vendas", icon: <ShoppingCart className="w-5 h-5" /> },
    { label: "Contemplação", to: "/lances", icon: <Gavel className="w-5 h-5" /> },
    { label: "Propostas", to: "/propostas", icon: <FileText className="w-5 h-5" /> },
    { label: "Inadimplentes", to: "/inadimplentes", icon: <AlertCircle className="w-5 h-5" /> },
    { label: "Comissões", to: "/comissoes", icon: <Banknote className="w-5 h-5" />, adminOnly: true, liberadoNaDemo: true },
    // Conta demo (migration 286): o caixa dela fica aqui, não no Acesso Restrito.
    { label: "Fluxo de Caixa", to: "/fluxo-caixa", icon: <Wallet className="w-5 h-5" />, soDemo: true, exigeFluxoCaixa: true },
    { label: "Campanhas", to: "/campanhas", icon: <Trophy className="w-5 h-5" />, modulo: "campanhas" },
    { label: "Cancelamentos", to: "/cancelamentos", icon: <XCircle className="w-5 h-5" />, adminOnly: true },
    { label: "Grupos", to: "/consortium_groups", icon: <Layers className="w-5 h-5" /> },
    { label: "Simulador", to: "/simulator", icon: <Calculator className="w-5 h-5" /> },
    { label: "North IA", to: "/ai", icon: <Bot className="w-5 h-5" />, modulo: "north_ia" },
    // Restrito a super admin por enquanto: o agente ainda está em validação.
    { label: "Meu Agente", to: "/meu-agente", icon: <Sparkles className="w-5 h-5" />, adminOnly: true, dot: briefingNaoLido, modulo: "north_ia" },
    // API v1 (migration 276): gerente ou super admin, com o módulo 'api'.
    { label: "Chaves de API", to: "/chaves-api", icon: <KeyRound className="w-5 h-5" />, modulo: "api", exigeGerente: true },
    { label: "Indicações", to: "/indicacoes", icon: <Handshake className="w-5 h-5" />, exigeParceira: true },
  ];

  // Rotas com prefixo /seguros de propósito: o app é um só e as rotas são
  // globais, então /propostas já é do consórcio. Sem o prefixo, colidiriam.
  // Apólices, Cobrança, Inadimplência e Comissões: super admin vê a rede; quem
  // tem corretora vinculada (Conexões › Azos, 298) vê só a dela, pela RLS da 206.
  const exigenciasAbertas = useExigenciasAbertas(productModule === "insurance");
  const insuranceItems: SidebarItem[] = [
    { label: "Apólices", to: "/seguros/apolices", icon: <ShieldCheck className="w-5 h-5" />, exigeCorretora: true },
    // Cotação e Propostas são de todo vendedor: cada um cota e acompanha o
    // que é seu (Propostas mostra a carteira da seguradora só ao super admin).
    { label: "Propostas", to: "/seguros/propostas", icon: <FileText className="w-5 h-5" /> },
    { label: "Cotação", to: "/seguros/cotacao", icon: <Calculator className="w-5 h-5" /> },
    { label: "Cobrança", to: "/seguros/cobranca", icon: <AlertCircle className="w-5 h-5" />, exigeCorretora: true },
    // Duas telas de propósito: "Cobrança" lista o CLIENTE em atraso, um por
    // linha; "Inadimplência" agrupa por CORRETORA parceira e mostra a curva.
    // Unidades diferentes, conversas diferentes.
    { label: "Inadimplência", to: "/seguros/inadimplencia", icon: <TrendingDown className="w-5 h-5" />, exigeCorretora: true },
    // Pedidos das seguradoras depois da proposta: pendências (MAG) e
    // contrapropostas (Azos). De todo vendedor; o número é o de abertas.
    { label: "Exigências", to: "/seguros/exigencias", icon: <ClipboardList className="w-5 h-5" />, contador: exigenciasAbertas },
    { label: "Comissões", to: "/seguros/take-rates", icon: <Banknote className="w-5 h-5" />, exigeCorretora: true },
    // O que a corretora recebe da seguradora, pelo login dela no portal
    // (Conexões › Azos, 288). De todo corretor: cada um vê a própria conexão.
    { label: "Recebimentos", to: "/seguros/recebimentos", icon: <HandCoins className="w-5 h-5" /> },
    // O que é só da Azos (campanhas, leads, cotador, histórico).
    { label: "Azos", to: "/seguros/azos", icon: <Wallet className="w-5 h-5" /> },
    { label: "Persistência", to: "/seguros/persistencia", icon: <XCircle className="w-5 h-5" />, adminOnly: true },
    { label: "Corretoras", to: "/seguros/corretoras", icon: <Users className="w-5 h-5" />, adminOnly: true },
    // A mesma North IA do consórcio: /ai é rota compartilhada, então o menu
    // continua o de Seguros quem vem daqui.
    { label: "North IA", to: "/ai", icon: <Bot className="w-5 h-5" />, modulo: "north_ia" },
    { label: "Meu Agente", to: "/seguros/meu-agente", icon: <Sparkles className="w-5 h-5" />, adminOnly: true, dot: briefingNaoLido, modulo: "north_ia" },
  ];

  // Consórcio Canopus (09/10/2026): em teste, tudo só para super admin — a
  // migration 245 deixou a leitura das tabelas canopus_* só para ele.
  const canopusItems: SidebarItem[] = [
    { label: "Início", to: "/canopus", icon: <Landmark className="w-5 h-5" />, adminOnly: true },
    { label: "Planos", to: "/canopus/planos", icon: <Layers className="w-5 h-5" />, adminOnly: true },
    { label: "Vendas", to: "/canopus/vendas", icon: <ShoppingCart className="w-5 h-5" />, adminOnly: true },
    { label: "Clientes", to: "/canopus/clientes", icon: <UserCheck className="w-5 h-5" />, adminOnly: true },
    { label: "Comissões", to: "/canopus/comissoes", icon: <Banknote className="w-5 h-5" />, adminOnly: true },
    { label: "Carteira", to: "/canopus/carteira", icon: <Wallet className="w-5 h-5" />, adminOnly: true },
    { label: "Contatos", to: "/contacts", icon: <Users className="w-5 h-5" />, adminOnly: true },
  ];

  const moduleItems = productModule === "insurance" ? insuranceItems : consortiumItems;

  // Seção sem produto (Demais Produtos, Acesso Restrito): menu próprio e
  // reduzido, no lugar do de produto — ver SECOES_SEM_PRODUTO.
  const secao = secaoDaRota(location.pathname);
  // Perfis de leitura: só consórcio, e só as telas do perfil (migration 184).
  const perfilLeitura = (identity?.perfil_leitura ?? null) as PerfilLeitura | null;
  const somenteLeitura = perfilLeitura !== null;
  const allItems = secao
    ? secao.itens
        .filter((i) => verMensagens || !i.to.endsWith("/mensagens"))
        .map((i) =>
          i.to.endsWith("/feed")
            ? { ...i, contador: feedNaoLidos }
            : i.to.endsWith("/mensagens")
              ? { ...i, contador: mensagensNaoLidas }
              : i,
        )
    : productModule === "canopus"
      ? canopusItems
      : [...baseItems, ...moduleItems];
  // Módulos da organização (migration 279): o escritório só vê os dele.
  const { data: organizacao } = useOrganizacaoAtiva();
  const cliente = ehCliente(organizacao);
  // Indicações: gerente de escritório parceiro que já recebeu alguém pelo link.
  const { data: indicacoes } = useQuery({
    queryKey: ["contratacao", "indicacoes"],
    queryFn: listarIndicacoes,
    enabled: cliente && !!identity?.is_manager,
    staleTime: 5 * 60_000,
  });
  const { data: corretora } = useCorretoraVinculada(productModule === "insurance" && !identity?.is_super_admin);
  const visibleItems = perfilLeitura
    ? [...baseItems, ...consortiumItems].filter((item) => rotaPermitidaLeitura(perfilLeitura, item.to))
    : allItems.filter((item) => {
        if (!temModulo(organizacao, item.modulo)) return false;
        if (item.soDemo && !identity?.is_demo) return false;
        if (item.adminOnly && !identity?.is_super_admin && !(item.liberadoNaDemo && identity?.is_demo)) return false;
        if (item.exigeCorretora && !identity?.is_super_admin && !corretora) return false;
        if (item.exigeGerente && !identity?.is_manager && !identity?.is_super_admin) return false;
        if (item.exigeAdministrador && !identity?.is_super_admin && identity?.gestao_unidade !== "administrador") return false;
        if (item.exigeParceira && !(cliente && identity?.is_manager && (indicacoes?.length ?? 0) > 0)) return false;
        // Lista própria da aba, não papel: ver secoes.tsx.
        if (item.exigeFluxoCaixa && !identity?.fluxo_caixa_nivel) return false;
        if (item.exigePrevisaoServopa && !identity?.previsao_servopa) return false;
        return true;
      });
  // UM item aceso só, o mais específico — ver rotaAcesa em secoes.tsx.
  const acesa = rotaAcesa(location.pathname, visibleItems);

  // Seções só agrupam: mesmos itens, mesma filtragem, mesma ordem dentro do grupo.
  const chaveMenu = secao ? secao.titulo : perfilLeitura ? "consortium" : productModule;
  const grupos = agruparEmSecoes(chaveMenu, visibleItems);

  const linkClasses = (ativo: boolean) =>
    cn(
      "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors relative group overflow-hidden text-sm font-medium cursor-pointer",
      ativo
        ? "bg-marinho text-white before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-ouro"
        : "text-foreground/75 hover:bg-muted hover:text-foreground",
    );

  return (
    <div className="flex flex-col h-full bg-sidebar text-foreground overflow-hidden dark:bg-card">
      {/* Topo no marinho da barra superior (22/10/2026): as duas faixas formam uma só. */}
      <div className="flex items-center justify-between gap-2 px-4 h-14 border-b border-white/10 bg-marinho overflow-hidden dark:border-border dark:bg-card">
        {!collapsed && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Escritório cliente: a logo e o nome dele (279). Até a organização
                chegar, nenhuma logo — nem a da North. */}
            {organizacao !== undefined && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {cliente ? (
                  organizacao?.logo_url && <img className="h-8 max-w-[96px] object-contain" src={organizacao.logo_url} alt={organizacao.nome} />
                ) : (
                  <>
                    <img className="[.light_&]:hidden h-8" src={darkModeLogo} alt={title} />
                    <img className="[.dark_&]:hidden h-8" src={lightModeLogo} alt={title} />
                  </>
                )}
              </div>
            )}
            <div className="text-sm font-semibold leading-tight text-white truncate dark:text-foreground">
              {cliente && !secao ? organizacao?.nome : secao ? secao.titulo : getProductModuleConfig(productModule).title}
            </div>
          </div>
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Abrir menu" : "Recolher menu"}
            className="h-8 w-8 p-0 text-white/70 hover:bg-white/10 hover:text-white flex-shrink-0 mx-auto dark:text-muted-foreground dark:hover:bg-muted"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 space-y-4">
        {grupos.map((grupo) => (
          <div key={grupo.titulo || "inicio"}>
            {grupo.titulo && !collapsed && (
              <p className="px-3 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {grupo.titulo}
              </p>
            )}
            {grupo.titulo && collapsed && <div className="mx-3 mb-2 border-t border-border" />}
            <ul className="space-y-0.5">
              {grupo.itens.map((item) => {
                const ativo = !item.external && item.to === acesa;
                const inner = (
                  <>
                    <span className="relative flex-shrink-0 [&>svg]:h-[18px] [&>svg]:w-[18px]">
                      {item.icon}
                      {!!item.contador && <ContadorNovos total={item.contador} anel={ativo ? "ring-marinho" : "ring-card"} />}
                      {item.dot && collapsed && (
                        <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-card" />
                      )}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.dot && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-amber-400" />}
                        {item.badge && (
                          <span className="text-xs bg-destructive text-destructive-foreground px-2 py-1 rounded-full flex-shrink-0">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && (
                      <div className="absolute left-full ml-2 bg-marinho text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {item.label}
                      </div>
                    )}
                  </>
                );
                return (
                  // Rótulo + destino: dois itens para o mesmo lugar não podem
                  // dividir a chave (o React deixava um item fantasma no menu).
                  <li key={`${item.label}:${item.to}`}>
                    {item.external ? (
                      <a
                        href={item.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClasses(false)}
                        title={collapsed ? item.label : undefined}
                      >
                        {inner}
                      </a>
                    ) : (
                      <Link to={item.to} className={linkClasses(ativo)} title={collapsed ? item.label : undefined}>
                        {inner}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Rodapé: Meu perfil, Início geral e Sair. "Ir para Seguros/Consórcio" e
          "Ir para o CRM" foram para a troca de produto da barra superior, com as
          mesmas regras de quem vê (TrocaProduto, abaixo). */}
      <div className="border-t border-border p-2.5 space-y-0.5 overflow-hidden">
        {!somenteLeitura && (
          <Link to="/settings" className={linkClasses(!!isActive("/settings"))} title={collapsed ? "Meu perfil" : undefined}>
            <User className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && <span className="flex-1 truncate">Meu perfil</span>}
          </Link>
        )}
        {!somenteLeitura && (
          <Link to={HUB_ROUTE} className={linkClasses(false)} title={collapsed ? "Início geral" : undefined}>
            <Home className="w-[18px] h-[18px] flex-shrink-0" />
            {!collapsed && <span className="flex-1 truncate">Início geral</span>}
          </Link>
        )}
        <button type="button" onClick={() => logout()} className={cn(linkClasses(false), "w-full")} title={collapsed ? "Sair" : undefined}>
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
          {!collapsed && <span className="truncate">Sair</span>}
        </button>
      </div>
    </div>
  );
};

/**
 * Troca de produto na barra superior. São os destinos que ficavam no rodapé do
 * menu, com as mesmas regras de antes:
 *   - "Ir para Seguros/Consórcio": fora de seção sem produto, fora de perfil de
 *     leitura, só para super admin, gerente ou quem tem corretora vinculada;
 *   - "Ir para o CRM": fora de perfil de leitura.
 * Sem destino nenhum, fica só o nome de onde a pessoa está.
 */
export const TrocaProduto = () => {
  const location = useLocation();
  const { identity } = useGetIdentity();
  const productModule = useProductModule() ?? "consortium";
  const secao = secaoDaRota(location.pathname);
  const somenteLeitura = (identity?.perfil_leitura ?? null) !== null;
  const noCrm = !!(matchPath(`${CRM_ROUTE}/*`, location.pathname) || matchPath(CRM_ROUTE, location.pathname));

  const atual = noCrm
    ? "CRM"
    : secao
      ? secao.titulo
      : productModule === "insurance"
        ? "Seguros"
        : productModule === "canopus"
          ? "Consórcio Canopus"
          : "Consórcio";

  const { data: organizacao } = useOrganizacaoAtiva();
  const destinos: { rotulo: string; to: string; ativo: boolean }[] = [];
  // Seguros é módulo (279): escritório sem ele não troca de produto.
  // Corretora vinculada (298) também troca: a carteira dela está em Seguros.
  const { data: corretora } = useCorretoraVinculada(!identity?.is_super_admin && !identity?.is_manager && !somenteLeitura);
  if (!secao && !somenteLeitura && (identity?.is_super_admin || identity?.is_manager || corretora)
      && temModulo(organizacao, "seguros")) {
    // Mesmo alvo do antigo "Ir para…": Consórcio ↔ Seguros; da Canopus, Consórcio.
    const alvo = otherProductModule(productModule);
    const nome = (m: typeof productModule) => (m === "insurance" ? "Seguros" : m === "canopus" ? "Consórcio Canopus" : "Consórcio");
    destinos.push(
      { rotulo: nome(productModule), to: PRODUCT_MODULES[productModule].home, ativo: !noCrm },
      { rotulo: nome(alvo), to: PRODUCT_MODULES[alvo].home, ativo: false },
    );
  }
  if (!somenteLeitura) destinos.push({ rotulo: "CRM", to: CRM_ROUTE, ativo: noCrm });

  const marca = <span className="h-2 w-2 rounded-[2px] bg-secondary" aria-hidden />;
  if (destinos.length === 0) {
    return (
      <span className="inline-flex h-8 items-center gap-2 rounded-lg border border-white/25 px-2.5 text-sm font-medium dark:border-border">
        {marca}
        {atual}
      </span>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 max-w-[60vw] cursor-pointer items-center gap-2 rounded-lg border border-white/25 px-2.5 text-sm font-medium hover:bg-white/10 dark:border-border dark:hover:bg-muted"
        >
          {marca}
          <span className="truncate">{atual}</span>
          <ChevronDown className="h-3.5 w-3.5 text-white/70 dark:text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {destinos.map((d) => (
          <DropdownMenuItem key={d.rotulo} asChild>
            <Link to={d.to} className="flex cursor-pointer items-center gap-2">
              {d.rotulo === "CRM" ? <ChevronsRight className="h-4 w-4" /> : <ArrowLeftRight className="h-4 w-4" />}
              <span className="flex-1">{d.rotulo}</span>
              {d.ativo && <Check className="h-4 w-4" />}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Sidebar = () => {
  const { collapsed, isMobile, mobileOpen, setMobileOpen } = useSidebar();

  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-sidebar border-r border-border dark:bg-card">
          <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
          <SidebarBody collapsed={false} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      className={cn(
        "border-r border-border h-screen fixed left-0 top-0 z-40 flex flex-col transition-all duration-300 overflow-hidden",
        collapsed ? "w-20" : "w-64"
      )}
    >
      <SidebarBody collapsed={collapsed} />
    </aside>
  );
};
